
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { MoreHorizontal, Trash2, CreditCard, CheckCircle2, AlertTriangle, Edit, RefreshCw, Copy } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useNotify } from '@/hooks/useNotify';
import { PaymentRecordModal, WriteOffModal } from '@/components/invoices/payments';
import { usePaymentRecording } from '@/components/invoices/payments/usePaymentRecording';

interface InvoiceActionsProps {
  invoiceId: string;
  currentStatus: string;
  invoice?: {
    id: string;
    invoice_number: string;
    total_amount: number;
    paid_amount: number;
    credit_applied: number;
    customer_id: string;
  };
  onStatusChange?: () => void;
  onDelete?: () => void;
}

const INVOICE_STATUSES = [
  { value: 'draft', label: 'Draft', variant: 'outline' as const },
  { value: 'sent', label: 'Sent', variant: 'secondary' as const },
  { value: 'partially_paid', label: 'Partially Paid', variant: 'secondary' as const },
  { value: 'paid', label: 'Paid', variant: 'default' as const },
  { value: 'overdue', label: 'Overdue', variant: 'destructive' as const },
  { value: 'cancelled', label: 'Cancelled', variant: 'destructive' as const },
];

const InvoiceActions: React.FC<InvoiceActionsProps> = ({
  invoiceId,
  currentStatus,
  invoice,
  onStatusChange,
  onDelete
}) => {
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showWriteOffModal, setShowWriteOffModal] = useState(false);
  const queryClient = useQueryClient();
  const notify = useNotify();
  
  const { quickPay, isQuickPaying } = usePaymentRecording(invoiceId);

  // Clone mutation
  const cloneInvoiceMutation = useMutation({
    mutationFn: async () => {
      // Fetch full invoice data
      const { data: sourceInvoice, error: fetchError } = await supabase
        .from('invoices')
        .select('*')
        .eq('id', invoiceId)
        .single();
      if (fetchError) throw fetchError;

      // Fetch line items
      const { data: sourceLineItems, error: liError } = await supabase
        .from('invoice_line_items')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('sort_order', { ascending: true });
      if (liError) throw liError;

      // Insert new standalone draft
      const { data: newInvoice, error: insertError } = await supabase
        .from('invoices')
        .insert({
          title: `Copy of ${sourceInvoice.title}`,
          description: sourceInvoice.description,
          customer_id: sourceInvoice.customer_id,
          company_id: sourceInvoice.company_id,
          created_by: sourceInvoice.created_by,
          estimate_id: null,
          invoice_type: 'standalone',
          status: 'draft',
          payment_terms: sourceInvoice.payment_terms,
          special_instructions: sourceInvoice.special_instructions,
          tax_inclusive: sourceInvoice.tax_inclusive,
          tax_percentage: sourceInvoice.tax_percentage,
          use_separate_site_address: sourceInvoice.use_separate_site_address,
          site_street_address: sourceInvoice.site_street_address,
          site_suburb: sourceInvoice.site_suburb,
          site_city: sourceInvoice.site_city,
          site_province_state: sourceInvoice.site_province_state,
          site_postal_code: sourceInvoice.site_postal_code,
        })
        .select('id')
        .single();
      if (insertError) throw insertError;

      // Copy line items
      if (sourceLineItems && sourceLineItems.length > 0) {
        const newLineItems = sourceLineItems.map((item, index) => ({
          invoice_id: newInvoice.id,
          description: item.description,
          base_unit: item.base_unit,
          unit_cost: item.unit_cost,
          quantity_invoiced: item.quantity_invoiced,
          total_cost: item.total_cost,
          sort_order: item.sort_order ?? index,
          base_cost: item.base_cost,
          markup_percentage: item.markup_percentage,
          category_id: item.category_id,
          source_type: item.source_type,
          source_item_id: item.source_item_id,
        }));

        const { error: lineItemsError } = await supabase
          .from('invoice_line_items')
          .insert(newLineItems);
        if (lineItemsError) throw lineItemsError;
      }

      return newInvoice;
    },
    onSuccess: (data) => {
      notify.success('Invoice cloned successfully');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate(`/invoices/edit/${data.id}`);
    },
    onError: (error) => {
      console.error('Error cloning invoice:', error);
      notify.error(error);
    },
  });

  const remainingBalance = invoice 
    ? (invoice.total_amount || 0) - (invoice.credit_applied || 0) - (invoice.paid_amount || 0) 
    : 0;

  const deleteInvoiceMutation = useMutation({
    mutationFn: async () => {
      // First, release any reserved credits back to the customer
      const { data: creditApps, error: fetchError } = await supabase
        .from('credit_applications')
        .select('id, credit_id, amount_applied')
        .eq('invoice_id', invoiceId)
        .eq('application_status', 'reserved');

      if (fetchError) {
        console.error('Error fetching credit applications:', fetchError);
      }

      // Release each reserved credit
      for (const app of creditApps || []) {
        // Get current credit remaining amount
        const { data: credit } = await supabase
          .from('customer_credits')
          .select('remaining_amount, status')
          .eq('id', app.credit_id)
          .single();

        if (credit) {
          // Restore the credit amount
          await supabase
            .from('customer_credits')
            .update({ 
              remaining_amount: (credit.remaining_amount || 0) + app.amount_applied,
              status: 'issued',
              updated_at: new Date().toISOString()
            })
            .eq('id', app.credit_id);
        }

        // Delete the credit application
        await supabase
          .from('credit_applications')
          .delete()
          .eq('id', app.id);
      }

      // Now delete the invoice
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', invoiceId);

      if (error) throw error;
    },
    onSuccess: () => {
      notify.success('Invoice deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['customerCredits'] });
      onDelete?.();
    },
    onError: (error) => {
      console.error('Error deleting invoice:', error);
      notify.error(error);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      console.log('[InvoiceActions] Starting status update:', {
        invoiceId,
        newStatus,
        currentStatus
      });
      
      // If changing from draft to sent, finalize any reserved credit applications
      if (currentStatus === 'draft' && newStatus === 'sent') {
        const { error: finalizeError } = await supabase
          .from('credit_applications')
          .update({ application_status: 'finalized' })
          .eq('invoice_id', invoiceId)
          .eq('application_status', 'reserved');

        if (finalizeError) {
          console.error('[InvoiceActions] Error finalizing credit applications:', finalizeError);
        }
      }
      
      const { error } = await supabase
        .from('invoices')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', invoiceId);

      console.log('[InvoiceActions] Update result:', { error });
      
      if (error) {
        console.error('[InvoiceActions] Update failed:', error);
        throw error;
      }
      
      console.log('[InvoiceActions] Update successful');
    },
    onSuccess: () => {
      notify.success('Invoice status updated successfully');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['customerCredits'] });
      onStatusChange?.();
    },
    onError: (error) => {
      console.error('Error updating invoice status:', error);
      notify.error(error);
    },
  });

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === currentStatus) return;
    
    // If changing to "paid", use quick pay instead of just status change
    if (newStatus === 'paid' && remainingBalance > 0) {
      try {
        await quickPay();
        onStatusChange?.();
      } catch (error) {
        // Error handled in hook
      }
      return;
    }
    
    updateStatusMutation.mutate(newStatus);
  };

  const handleDelete = () => {
    deleteInvoiceMutation.mutate();
    setShowDeleteDialog(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* Edit Invoice */}
          <DropdownMenuItem onClick={() => navigate(`/invoices/edit/${invoiceId}`)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Invoice
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => cloneInvoiceMutation.mutate()}
            disabled={cloneInvoiceMutation.isPending}
          >
            <Copy className="h-4 w-4 mr-2" />
            {cloneInvoiceMutation.isPending ? 'Cloning...' : 'Clone Invoice'}
          </DropdownMenuItem>

          {/* Payment Actions - Show only if there's a balance */}
          {remainingBalance > 0 && invoice && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowPaymentModal(true)}
                className="text-primary"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Record Payment
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleStatusChange('paid')}
                disabled={isQuickPaying}
                className="text-primary"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {isQuickPaying ? 'Processing...' : 'Mark as Paid'}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setShowWriteOffModal(true)}
                className="text-warning"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Write Off Balance
              </DropdownMenuItem>
            </>
          )}
          
          <DropdownMenuSeparator />

          {/* Change Status - Submenu */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <RefreshCw className="h-4 w-4 mr-2" />
              Change Status
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                {INVOICE_STATUSES
                  .filter(status => status.value !== 'draft' || currentStatus === 'draft')
                  .map((status) => (
                  <DropdownMenuItem
                    key={status.value}
                    onClick={() => handleStatusChange(status.value)}
                    disabled={status.value === currentStatus || updateStatusMutation.isPending || isQuickPaying}
                  >
                    <Badge variant={status.variant} className="mr-2">
                      {status.label}
                    </Badge>
                    {status.value === currentStatus && '(Current)'}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem
            onClick={() => setShowDeleteDialog(true)}
            className="text-destructive focus:text-destructive"
            disabled={deleteInvoiceMutation.isPending}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Invoice
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this invoice? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteInvoiceMutation.isPending}
            >
              {deleteInvoiceMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {invoice && (
        <>
          <PaymentRecordModal
            open={showPaymentModal}
            onOpenChange={setShowPaymentModal}
            invoice={invoice}
          />
          <WriteOffModal
            open={showWriteOffModal}
            onOpenChange={setShowWriteOffModal}
            invoice={invoice}
          />
        </>
      )}
    </>
  );
};

export default InvoiceActions;
